import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { EmptyRow, LoadingOverlay, RECEIVABLES_API, VISIT_DAYS, escapeHtml, fmtDate, money, todayIso, useReceivableLookups } from "./common";

const DENOMINATIONS = ["500 X", "200 X", "100 X", " 50 X", " 20 X", " 10 X", "  5 X", "COINS", "TOTAL", "SHORTAGE"];
const ROWS_PER_PAGE = 48;

/**
 * One A4 page per beat (continuing onto more pages after 48 bills) with blank Cash / UPI / Cheque / Cheque No columns for the
 * salesman to fill in, a bill count and totals, and the cash denomination box — the legacy "Export PDF" collection sheet.
 */
const buildPrintHtml = (data) => {
  const pages = data.beats
    .map((group) => {
      const chunks = [];
      for (let i = 0; i < group.bills.length; i += ROWS_PER_PAGE) chunks.push(group.bills.slice(i, i + ROWS_PER_PAGE));
      return chunks
        .map((bills, chunkIndex) => {
          const last = chunkIndex === chunks.length - 1;
          const body = bills
            .map(
              (b) => `<tr>
                <td>${escapeHtml(b.customerName.slice(0, 22))}</td><td class="c">${escapeHtml(b.invoiceNumber)}</td>
                <td class="c">${fmtDate(b.invoiceDate)}</td><td class="r">${money(b.invoiceAmount)}</td>
                <td class="r">${money(b.balanceAmount)}</td><td></td><td></td><td></td><td></td><td class="c">${b.overdueDays}</td></tr>`
            )
            .join("");
          const summary = last
            ? `<tr class="sum"><td></td><td class="c">No. of Bills: ${group.bills.length}</td><td></td>
                <td class="r">${money(group.invoiceTotal)}</td><td class="r">${money(group.balanceTotal)}</td>
                <td colspan="5"></td></tr>`
            : "";
          const denominations = last
            ? `<table class="denom">${DENOMINATIONS.map(
                (d) => `<tr><td class="${d === "TOTAL" ? "b" : ""}">${d.replace(/ /g, "&nbsp;")}</td><td></td><td class="c">=</td><td></td></tr>`
              ).join("")}</table>`
            : "";
          return `<section>
            <h1>${escapeHtml((data.companyName || "").toUpperCase())}</h1>
            <h2>BEAT : ${escapeHtml(group.beatName)} &nbsp;&nbsp;&nbsp; SALESMAN : ${escapeHtml(group.salesmanName || "N/A")}
              &nbsp;&nbsp;&nbsp; DATE : ${fmtDate(data.date)}</h2>
            <h2>CUSTOMER PAYMENT STATEMENT</h2>
            <table class="bills">
              <colgroup><col style="width:22%"><col style="width:16%"><col style="width:9%"><col style="width:10%">
                <col style="width:10%"><col style="width:7%"><col style="width:7%"><col style="width:8%"><col style="width:6%"><col style="width:5%"></colgroup>
              <thead><tr><th>Customer</th><th>Bill No.</th><th>Date</th><th>Inv.Amt</th><th>Balance</th><th>Cash</th><th>UPI</th>
                <th>Cheque</th><th>Chq.No</th><th>Days</th></tr></thead>
              <tbody>${body}${summary}</tbody>
            </table>
            ${denominations}
          </section>`;
        })
        .join("");
    })
    .join("");

  return `<!doctype html><html><head><title>Bills For Collection ${fmtDate(data.date)}</title><style>
    @page { size: A4; margin: 8mm; }
    body { font-family: Helvetica, Arial, sans-serif; margin: 0; color: #000; }
    section { page-break-after: always; }
    section:last-child { page-break-after: auto; }
    h1 { font-size: 16px; text-align: center; margin: 4px 0; }
    h2 { font-size: 12px; font-weight: normal; text-align: center; margin: 4px 0; }
    table.bills { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 6px; }
    table.bills th, table.bills td { border: 1px solid #000; font-size: 9px; padding: 3px 4px; overflow: hidden; white-space: nowrap; }
    table.bills th { font-weight: normal; font-size: 10px; }
    .c { text-align: center; } .r { text-align: right; } .b { font-weight: bold; }
    tr.sum td { font-weight: bold; }
    table.denom { width: 50%; border-collapse: collapse; margin-top: 10px; }
    table.denom td { border: 1px solid #000; font-size: 9px; padding: 3px 6px 3px 15px; height: 12px; }
  </style></head><body>${pages}</body></html>`;
};

/** The salesman's collection sheet (legacy BillsForCollection.aspx). */
const BillsForCollection = () => {
  const { salesmen, beats } = useReceivableLookups(["salesmen", "beats"]);
  const [date, setDate] = useState(todayIso());
  const [salesmanUserId, setSalesmanUserId] = useState("");
  const [visitDay, setVisitDay] = useState("");
  const [beatId, setBeatId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const beatOptions = beats
    .filter((b) => !salesmanUserId || String(b.salesmanUserId) === String(salesmanUserId))
    .filter((b) => !visitDay || b.visitDay === visitDay);

  const params = () => ({ date, salesmanUserId, beatId, visitDay });

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${RECEIVABLES_API}/bills-for-collection`, params());
      setData(response);
      if (!response?.beats?.length) toast.info("No bills found for the selected filters");
    } catch (error) {
      toast.error(error.message || "Failed to load bills");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      await api.downloadFile(`${RECEIVABLES_API}/bills-for-collection/export`, params(), `bills-for-collection-${date}.xlsx`);
    } catch (error) {
      toast.error(error.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const downloadPdf = async () => {
    setExporting(true);
    try {
      await api.downloadFile(`${RECEIVABLES_API}/bills-for-collection/pdf`, params(), `bills-for-collection-${date}.pdf`);
    } catch (error) {
      toast.error(error.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const printSheet = () => {
    if (!data?.beats?.length) {
      toast.error("No data available. Please submit the form first.");
      return;
    }
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Allow pop-ups to print the collection sheet");
      return;
    }
    win.document.write(buildPrintHtml(data));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading bills..." />
      <div className="filters-bar">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Bills up to" />
        <select
          value={salesmanUserId}
          onChange={(e) => {
            setSalesmanUserId(e.target.value);
            setBeatId("");
          }}
          aria-label="Salesman"
        >
          <option value="">All salesmen</option>
          {salesmen.map((s) => (
            <option key={s.userId} value={s.userId}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={visitDay}
          onChange={(e) => {
            setVisitDay(e.target.value);
            setBeatId("");
          }}
          aria-label="Day"
        >
          <option value="">All days</option>
          {VISIT_DAYS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <select value={beatId} onChange={(e) => setBeatId(e.target.value)} aria-label="Beat">
          <option value="">All beats</option>
          {beatOptions.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Submit"}
        </button>
        <button className="secondary" onClick={downloadPdf} disabled={exporting || !data?.beats?.length}>
          Export PDF
        </button>
        <button className="secondary" onClick={printSheet} disabled={!data?.beats?.length}>
          Print
        </button>
        <button className="secondary" onClick={exportExcel} disabled={exporting || !data?.beats?.length}>
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Sales Person</th>
              <th>Beat</th>
              <th>Bill No</th>
              <th>Date</th>
              <th style={{ textAlign: "right" }}>Inv Amt</th>
              <th style={{ textAlign: "right" }}>Balance</th>
              <th style={{ textAlign: "center" }}>Overdue Days</th>
            </tr>
          </thead>
          <tbody>
            {!data?.beats?.length ? (
              <EmptyRow colSpan={8} title={data ? "No bills for collection" : "Choose filters and press Submit"} />
            ) : (
              data.beats.flatMap((group) => [
                <tr key={`beat-${group.beatId}`} style={{ background: "var(--color-neutral-soft)" }}>
                  <td colSpan={5} style={{ fontWeight: 700 }}>
                    {group.beatName} — {group.salesmanName || "N/A"} ({group.bills.length} bills)
                  </td>
                  <td style={{ fontWeight: 700, textAlign: "right" }}>{money(group.invoiceTotal)}</td>
                  <td style={{ fontWeight: 700, textAlign: "right" }}>{money(group.balanceTotal)}</td>
                  <td />
                </tr>,
                ...group.bills.map((bill) => (
                  <tr key={bill.invoiceId}>
                    <td title={bill.customerName}>{bill.customerName}</td>
                    <td>{group.salesmanName || "-"}</td>
                    <td>{group.beatName}</td>
                    <td>{bill.invoiceNumber}</td>
                    <td>{fmtDate(bill.invoiceDate)}</td>
                    <td style={{ textAlign: "right" }}>{money(bill.invoiceAmount)}</td>
                    <td style={{ textAlign: "right" }}>{money(bill.balanceAmount)}</td>
                    <td style={{ textAlign: "center" }}>{bill.overdueDays}</td>
                  </tr>
                )),
              ])
            )}
          </tbody>
          {data?.beats?.length ? (
            <tfoot>
              <tr>
                <td colSpan={5} style={{ fontWeight: 700 }}>
                  Total Bills: {data.totalBills}
                </td>
                <td style={{ fontWeight: 700, textAlign: "right" }}>{money(data.totalInvoiceAmount)}</td>
                <td style={{ fontWeight: 700, textAlign: "right" }}>{money(data.totalBalance)}</td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default BillsForCollection;
